import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChangeLogResponse, 
  ReviewStats, 
  ReviewFilters, 
  reviewAutomationService 
} from '../services/api/reviewAutomation.service';
import { ReviewStatsCard } from './ReviewStatsCard';
import { ReviewFiltersPanel } from './ReviewFiltersPanel';
import { ReviewChangesTable } from './ReviewChangesTable';
import { BulkActionsPanel } from './BulkActionsPanel';

interface ReviewAutomationDashboardProps {
  serverId?: number;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

export const ReviewAutomationDashboard: React.FC<ReviewAutomationDashboardProps> = ({
  serverId,
  onError,
  onSuccess
}) => {
  const [changes, setChanges] = useState<ChangeLogResponse[]>([]);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ReviewFilters>({
    server_id: serverId,
    review_status: 'pending'
  });
  const [selectedChanges, setSelectedChanges] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalChanges, setTotalChanges] = useState(0);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load changes and stats in parallel
      const [changesData, statsData] = await Promise.all([
        reviewAutomationService.getChanges(filters, currentPage * pageSize, pageSize),
        reviewAutomationService.getReviewStats(serverId)
      ]);
      
      setChanges(changesData);
      setStats(statsData);
      setTotalChanges(changesData.length); // In a real implementation, this would come from the API
    } catch (error) {
      console.error('Error loading review data:', error);
      onError?.('Failed to load review data');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, pageSize, serverId, onError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (newFilters: ReviewFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
    setSelectedChanges([]);
  };

  const handleSelectionChange = (changeIds: number[]) => {
    setSelectedChanges(changeIds);
  };

  const handleReviewChange = async (changeId: number, action: 'accept' | 'reject', notes?: string) => {
    try {
      await reviewAutomationService.reviewChange(changeId, {
        action,
        notes,
        reviewed_by: 'current_user' // In a real app, get this from auth context
      });
      
      onSuccess?.(`Change ${action}ed successfully`);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Error reviewing change:', error);
      onError?.(`Failed to ${action} change`);
    }
  };

  const handleBulkReview = async (action: 'accept' | 'reject', notes?: string) => {
    if (selectedChanges.length === 0) {
      onError?.('No changes selected');
      return;
    }

    try {
      const result = await reviewAutomationService.bulkReviewChanges({
        change_ids: selectedChanges,
        action,
        notes,
        reviewed_by: 'current_user' // In a real app, get this from auth context
      });
      
      onSuccess?.(`${result.successful_count} changes ${action}ed successfully`);
      setSelectedChanges([]);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Error bulk reviewing changes:', error);
      onError?.(`Failed to bulk ${action} changes`);
    }
  };

  const handleAutoApprove = async () => {
    try {
      const result = await reviewAutomationService.autoApproveTimezoneChanges(serverId);
      onSuccess?.(`Auto-approved ${result.auto_approved_count} timezone-only changes`);
      await loadData(); // Reload data
    } catch (error) {
      console.error('Error auto-approving changes:', error);
      onError?.('Failed to auto-approve changes');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await reviewAutomationService.exportChanges(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'review_changes.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      onSuccess?.('Changes exported successfully');
    } catch (error) {
      console.error('Error exporting changes:', error);
      onError?.('Failed to export changes');
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Review Automation</h1>
        <div className="flex space-x-2">
          <button
            onClick={handleAutoApprove}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            Auto-Approve Timezone Changes
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Export Changes
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && <ReviewStatsCard stats={stats} />}

      {/* Filters */}
      <ReviewFiltersPanel
        filters={filters}
        onFiltersChange={handleFilterChange}
        serverId={serverId}
      />

      {/* Bulk Actions */}
      {selectedChanges.length > 0 && (
        <BulkActionsPanel
          selectedCount={selectedChanges.length}
          onBulkAccept={(notes) => handleBulkReview('accept', notes)}
          onBulkReject={(notes) => handleBulkReview('reject', notes)}
          onClearSelection={() => setSelectedChanges([])}
        />
      )}

      {/* Changes Table */}
      <ReviewChangesTable
        changes={changes}
        loading={loading}
        selectedChanges={selectedChanges}
        onSelectionChange={handleSelectionChange}
        onReviewChange={handleReviewChange}
        onRefresh={loadData}
      />

      {/* Pagination */}
      {totalChanges > pageSize && (
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalChanges)} of {totalChanges} changes
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={(currentPage + 1) * pageSize >= totalChanges}
              className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};